import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, AtSign, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../lib/api';
import { getUser } from '../lib/auth';
import { toast } from 'sonner';

const AVATAR_COLORS = ['#003366', '#f5a800', '#1e8c45', '#e91e8c', '#c0392b', '#526071', '#ff6200', '#00244d'];

export default function TeamchatPage() {
  const user = getUser();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionCursorPos, setMentionCursorPos] = useState(0);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await api.get('/api/chat?limit=50');
      setMessages(res.data.messages || []);
    } catch (err) { console.error('[Chat] Fetch failed:', err); }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const [msgRes, usersRes] = await Promise.all([
          api.get('/api/chat?limit=50'),
          api.get('/api/users/basic'),
        ]);
        setMessages(msgRes.data.messages || []);
        setAllUsers(usersRes.data.users || []);
        // Mark as read
        api.post('/api/chat/mark-read').catch(() => {});
      } catch (err) { console.error('[Chat] Init failed:', err); }
      setLoading(false);
    };
    init();
    // Poll every 10 seconds
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get('/api/chat?limit=50');
        setMessages(res.data.messages || []);
        api.post('/api/chat/mark-read').catch(() => {});
      } catch (err) { /* polling — silent */ }
    }, 10000);
    return () => clearInterval(pollRef.current);
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleTextChange = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    setText(val);
    setMentionCursorPos(pos);
    const textBefore = val.slice(0, pos);
    const atMatch = textBefore.match(/@(\w*)$/);
    if (atMatch) {
      setMentionFilter(atMatch[1].toLowerCase());
      setShowMentionDropdown(true);
    } else {
      setShowMentionDropdown(false);
    }
  };

  const insertMention = (userName) => {
    const textBefore = text.slice(0, mentionCursorPos);
    const textAfter = text.slice(mentionCursorPos);
    const atIdx = textBefore.lastIndexOf('@');
    const newText = textBefore.slice(0, atIdx) + `@${userName} ` + textAfter;
    setText(newText);
    setShowMentionDropdown(false);
    inputRef.current?.focus();
  };

  const sendMessage = async () => {
    if (!text.trim()) return;
    const mentionMatches = text.match(/@(\w+)/g) || [];
    const mentionNames = mentionMatches.map(m => m.slice(1).toLowerCase());
    const mentionEmails = allUsers
      .filter(u => mentionNames.includes((u.naam || '').toLowerCase()))
      .map(u => u.email);
    try {
      const res = await api.post('/api/chat', {
        message: text.trim(),
        mentions: mentionEmails,
      });
      setMessages(prev => [...prev, res.data.message]);
      setText('');
      setShowMentionDropdown(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Verzenden mislukt'); }
  };

  const filteredUsers = allUsers.filter(u =>
    (u.naam || '').toLowerCase().includes(mentionFilter)
  );

  const getUserColor = (email) => {
    const idx = allUsers.findIndex(u => u.email === email);
    return AVATAR_COLORS[idx >= 0 ? idx % AVATAR_COLORS.length : 0];
  };

  if (loading) return <div className="p-6"><div className="animate-pulse space-y-4"><div className="h-20 bg-gray-100 rounded-xl" /></div></div>;

  return (
    <div className="tw1-fade-in flex flex-col h-[calc(100vh-120px)]" data-testid="teamchat-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#003366]/10 flex items-center justify-center">
          <MessageCircle className="w-5 h-5 text-[#003366]" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-[#0b1220]">Teamchat</h1>
          <p className="text-xs text-[#526071]">{allUsers.length} teamleden &middot; Typ @ om iemand te noemen</p>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto bg-white rounded-2xl border border-gray-100 p-4 space-y-3" data-testid="chat-messages-container">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <MessageCircle className="w-12 h-12 text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-[#526071]">Nog geen berichten</p>
              <p className="text-xs text-[#526071]/60">Start het gesprek!</p>
            </div>
          </div>
        ) : (
          messages.map((msg, i) => {
            const isMe = msg.user_email === user?.email;
            const prevMsg = messages[i - 1];
            const showAvatar = !prevMsg || prevMsg.user_email !== msg.user_email;
            const timeStr = msg.created_at ? new Date(msg.created_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : '';
            const dateStr = msg.created_at ? new Date(msg.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '';
            // Show date separator
            const prevDate = prevMsg?.created_at ? new Date(prevMsg.created_at).toDateString() : '';
            const thisDate = msg.created_at ? new Date(msg.created_at).toDateString() : '';
            const showDate = thisDate !== prevDate;
            return (
              <React.Fragment key={msg.id}>
                {showDate && (
                  <div className="flex items-center gap-3 my-2">
                    <div className="flex-1 h-px bg-gray-100" />
                    <span className="text-[10px] text-[#526071] font-medium">{dateStr}</span>
                    <div className="flex-1 h-px bg-gray-100" />
                  </div>
                )}
                <div className={`flex ${isMe ? 'justify-end' : 'justify-start'} ${!showAvatar && !showDate ? 'mt-0.5' : 'mt-2'}`} data-testid={`chat-msg-${msg.id}`}>
                  {!isMe && showAvatar && (
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 mr-2"
                      style={{ backgroundColor: getUserColor(msg.user_email) }}
                    >
                      {(msg.user_name || '?')[0]}
                    </div>
                  )}
                  {!isMe && !showAvatar && <div className="w-8 mr-2 flex-shrink-0" />}
                  <div className={`max-w-[70%] ${isMe ? 'order-1' : ''}`}>
                    {showAvatar && !isMe && (
                      <p className="text-[10px] font-medium text-[#526071] mb-0.5 ml-1">{msg.user_name}</p>
                    )}
                    <div className={`rounded-2xl px-3.5 py-2 ${
                      isMe
                        ? 'bg-[#003366] text-white rounded-br-md'
                        : 'bg-gray-100 text-[#0b1220] rounded-bl-md'
                    }`}>
                      <p className="text-sm leading-relaxed">
                        {msg.message.split(/(@\w+)/g).map((part, j) =>
                          part.startsWith('@') ? (
                            <span key={j} className={`font-semibold ${isMe ? 'text-[#f5a800]' : 'text-[#003366]'}`}>{part}</span>
                          ) : (
                            <span key={j}>{part}</span>
                          )
                        )}
                      </p>
                    </div>
                    <p className={`text-[9px] mt-0.5 ${isMe ? 'text-right mr-1' : 'ml-1'} text-[#526071]/60`}>{timeStr}</p>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="mt-3 relative" data-testid="chat-input-area">
        {/* Mention dropdown */}
        {showMentionDropdown && (
          <div className="absolute bottom-full left-0 w-full mb-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-[200px] overflow-y-auto" data-testid="chat-mention-dropdown">
            {filteredUsers.length === 0 ? (
              <p className="px-3 py-2 text-xs text-[#526071]">Geen gebruikers gevonden</p>
            ) : (
              filteredUsers.map(u => (
                <button
                  key={u.email}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#003366]/5 transition-colors text-left"
                  onClick={() => insertMention(u.naam || u.email.split('@')[0])}
                  data-testid={`chat-mention-${u.email}`}
                >
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: getUserColor(u.email) }}>
                    {(u.naam || u.email)[0]}
                  </div>
                  <span className="text-xs font-medium text-[#0b1220]">{u.naam || u.email.split('@')[0]}</span>
                </button>
              ))
            )}
          </div>
        )}
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Input
              ref={inputRef}
              value={text}
              onChange={handleTextChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !showMentionDropdown) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Typ een bericht... @ voor mentions"
              className="h-11 rounded-xl text-sm pr-10 bg-white border-gray-200"
              data-testid="chat-input"
            />
            <button
              onClick={() => { setShowMentionDropdown(!showMentionDropdown); setMentionFilter(''); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#003366] transition-colors"
            >
              <AtSign className="w-4 h-4" />
            </button>
          </div>
          <Button
            onClick={sendMessage}
            disabled={!text.trim()}
            className="h-11 px-4 rounded-xl bg-[#003366] hover:bg-[#00244d] text-white"
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
